import { useState, useEffect } from "react";
import api from "../../services/api";
import { useToast } from "../providers/ToastProvider";

interface MembershipLimit {
  id?: string;
  membership: "FREE" | "GROWTH" | "PREMIUM" | "VIP";
  creationLimit: number;
}

export default function MembershipLimitManager() {
  const { showToast } = useToast();
  const [limits, setLimits] = useState<Record<string, number>>({
    FREE: 0,
    GROWTH: 5,
    PREMIUM: 15,
    VIP: 35
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  const fetchLimits = async () => {
    try {
      const res = await api.get("/admin/membership-limits");
      const mappedLimits = { ...limits };
      res.data.forEach((item: MembershipLimit) => {
        mappedLimits[item.membership] = item.creationLimit;
      });
      setLimits(mappedLimits);
    } catch (err) {
      console.error("Failed to fetch membership limits:", err);
      showToast("Connection Error", "Unable to load creation limits from the server.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLimits();
  }, []);

  const handleUpdateLimit = async (membership: "FREE" | "GROWTH" | "PREMIUM" | "VIP", value: number) => {
    setUpdating(membership);
    try {
      await api.put("/admin/membership-limits", { membership, creationLimit: value });
      setLimits(prev => ({ ...prev, [membership]: value }));
      showToast("Quota Updated", `Successfully updated ${membership} tier limit to ${value}!`, "success");
    } catch (err: any) {
      console.error("Update limit failed:", err);
      showToast("Update Failed", err.response?.data?.error || "Failed to update limit.", "error");
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center flex flex-col items-center gap-4">
        <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
        <p className="text-xs font-black uppercase tracking-widest text-on-surface-variant/60">Loading Limits...</p>
      </div>
    );
  }

  const tiers = [
    {
      key: "FREE",
      name: "Free Tier",
      description: "Default membership tier with zero AI strategy permissions.",
      color: "from-slate-500/20 to-slate-600/10 hover:border-slate-500/30",
      accent: "bg-slate-500",
      textAccent: "text-slate-500",
      glow: "shadow-slate-500/5",
      defaultVal: 0,
      min: 0,
      max: 10
    },
    {
      key: "GROWTH",
      name: "Growth Tier",
      description: "Ideal for beginners and initial strategy drafting.",
      color: "from-emerald-500/20 to-teal-500/10 hover:border-emerald-500/30",
      accent: "bg-emerald-500",
      textAccent: "text-emerald-500",
      glow: "shadow-emerald-500/5",
      defaultVal: 5,
      min: 1,
      max: 50
    },
    {
      key: "PREMIUM",
      name: "Premium Tier",
      description: "Our most popular package for growing businesses.",
      color: "from-indigo-500/20 to-purple-500/10 hover:border-indigo-500/30",
      accent: "bg-indigo-500",
      textAccent: "text-indigo-500",
      glow: "shadow-indigo-500/5",
      defaultVal: 15,
      min: 1,
      max: 100
    },
    {
      key: "VIP",
      name: "VIP Tier",
      description: "Maximum output and fully-managed scaling systems.",
      color: "from-amber-500/20 to-orange-500/10 hover:border-amber-500/30",
      accent: "bg-amber-500",
      textAccent: "text-amber-500",
      glow: "shadow-amber-500/5",
      defaultVal: 35,
      min: 1,
      max: 200
    }
  ] as const;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* Minimalist Hero Header */}
      <div className="flex justify-between items-end pb-4 border-b border-outline-variant/10">
        <div>
          <h2 className="text-xl font-black text-on-surface tracking-tight">Combined Creation Quotas</h2>
          <p className="text-[9px] font-bold text-on-surface-variant uppercase tracking-[0.2em] mt-1">
            Control strategy + poster creation limits across membership tiers
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {tiers.map((tier) => {
          const currentLimit = limits[tier.key] ?? tier.defaultVal;
          return (
            <div
              key={tier.key}
              className={`group relative overflow-hidden bg-gradient-to-br ${tier.color} border border-outline-variant/10 rounded-[2rem] p-6 shadow-xl ${tier.glow} transition-all hover:scale-[1.01]`}
            >
              {/* Premium Top Glow */}
              <div className={`absolute top-0 left-0 right-0 h-[2px] ${tier.accent} opacity-30`} />

              <div className="space-y-6 flex flex-col justify-between h-full">
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <span className={`text-[9px] font-black uppercase tracking-widest ${tier.textAccent}`}>
                        {tier.name}
                      </span>
                      <h3 className="text-lg font-black text-on-surface tracking-tight">
                        {tier.key}
                      </h3>
                    </div>
                    <div className={`w-10 h-10 rounded-2xl ${tier.accent}/10 flex items-center justify-center ${tier.textAccent}`}>
                      <span className="material-symbols-outlined text-xl">
                        {tier.key === "FREE" ? "lock" : tier.key === "GROWTH" ? "trending_up" : tier.key === "PREMIUM" ? "workspace_premium" : "diamond"}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs font-medium text-on-surface-variant/80 min-h-[36px]">
                    {tier.description}
                  </p>
                </div>

                {/* Quota Input Field & Action */}
                <div className="space-y-3 pt-4 border-t border-outline-variant/5">
                  <label className="text-[9px] font-black text-on-surface-variant/60 uppercase tracking-widest block">
                    Creation limit
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min={tier.min}
                      max={tier.max}
                      value={currentLimit}
                      onChange={(e) => {
                        let val = parseInt(e.target.value, 10);
                        if (isNaN(val)) val = tier.min;
                        setLimits(prev => ({ ...prev, [tier.key]: val }));
                      }}
                      onBlur={(e) => {
                        let val = parseInt(e.target.value, 10);
                        if (isNaN(val) || val < tier.min) val = tier.min;
                        if (val > tier.max) val = tier.max;
                        handleUpdateLimit(tier.key, val);
                      }}
                      className="w-20 bg-surface-container-low/45 border border-outline-variant/15 text-center py-3 rounded-xl text-sm font-black text-on-surface outline-none focus:border-primary transition-all"
                    />
                    <button
                      disabled={updating === tier.key}
                      onClick={() => handleUpdateLimit(tier.key, currentLimit)}
                      className="flex-1 py-3 bg-on-surface/5 hover:bg-on-surface/10 text-on-surface rounded-xl font-bold text-[9px] uppercase tracking-widest transition-colors flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {updating === tier.key ? (
                        <div className="w-3 h-3 rounded-full border-2 border-on-surface border-t-transparent animate-spin" />
                      ) : (
                        <>
                          <span className="material-symbols-outlined text-[14px]">save</span>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                  <div className="flex justify-between text-[8px] font-bold text-on-surface-variant/40 uppercase tracking-widest px-1">
                    <span>Min: {tier.min}</span>
                    <span>Max: {tier.max}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modern Notice Banner */}
      <div className="p-6 bg-surface-container-low border border-outline-variant/10 rounded-[2rem] flex flex-col md:flex-row md:items-center gap-4 relative overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />
        <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center flex-shrink-0">
          <span className="material-symbols-outlined text-2xl">info</span>
        </div>
        <div className="space-y-1">
          <h4 className="text-xs font-black text-on-surface tracking-tight uppercase">Admin & Coach Exemption Notice</h4>
          <p className="text-[11px] font-medium text-on-surface-variant">
            Coaches, Admins, Coaching Associates, and Marketing Associates bypass all creation limits. This setting strictly governs student creation quotas (Strategy Creations + AI Poster Prompt Generations).
          </p>
        </div>
      </div>
    </div>
  );
}
